import { useMemo } from "react";
import type { PressableProps } from "react-native";

import type { TFn } from "../i18nTypes";
import type { ConfirmFn } from "../seams";

// EVERY hold the grid answers to, and the screen-reader door beside it.
//
// ⛔ Split out of IntensityGridView because it is a different job from
// rendering: it is the whole contract of a HOLD, and that contract is things
// that must not drift apart —
//   · the hold itself (onLongPress),
//   · for a destructive one, the dialog — the ONLY step between a gesture with
//     no armed state to take back and a destroyed week,
//   · and the screen-reader door, because VoiceOver cannot reach onLongPress
//     on iOS, so every hold here also names a custom action.
// Returning them as ONE prop bundle per target is what keeps a caller from
// wiring the hold and forgetting the others.
//
// ⭐ PE-31 — PLACEMENT joined removal here, and that is why the file is no
// longer `useGridRemoval`. Placement used to buy its a11y door with a visible
// <ToggleRow> under the matrix ("D2 · W1" + a switch). Antenore, smoke 29/08:
// «Switch per rimuovere giorno sotto la matrice, avevo chiesto solo il long
// press». He is right twice over — he asked for the hold and nothing else, and
// a switch that says a COORDINATE reads as "remove this day", which is not what
// it did. So placement now takes the same named-action door removal already
// had, and the switch is gone: one gesture, one door, no second control.
/** What a hold contributes to the element it acts on: the gesture and the
 *  named door beside it, always together. ⛔ Typed off `PressableProps`, not a
 *  `Record<string, unknown>` — the whole point of returning a bundle is that a
 *  caller cannot wire half of it, and an untyped bag lets a misspelled key
 *  spread onto a `<Pressable>` and compile. Same reason `Chip`/`IconButton`
 *  reach for this type for their own passthroughs. */
export type GridGestureBundle = Pick<
  PressableProps,
  "onLongPress" | "accessibilityActions" | "onAccessibilityAction"
>;

export function useGridGestures({
  t,
  confirm,
  canRemoveWeek,
  canRemoveDay,
  onRemoveWeek,
  onRemoveDay,
  onCloneWeek,
  placement,
}: {
  t: TFn;
  confirm?: ConfirmFn;
  canRemoveWeek: boolean;
  canRemoveDay: boolean;
  onRemoveWeek?: (week: number) => void;
  onRemoveDay?: (dayIdx: number) => void;
  /** PE-45 — hold `W ＋` to add a week that REPEATS the one on screen, where a
   *  tap adds an empty one. Absent ⇒ the chip keeps its tap and gains no hold,
   *  and the hint row stops naming a gesture that would do nothing.
   *  ⛔ No confirm, unlike the removals: this one adds, and the way back is the
   *  hold-to-remove on the new week's header. */
  onCloneWeek?: () => void;
  /** The focused exercise's placement layer, or null when nothing is focused
   *  (Home, and the editor before a pick) — then a cell has no hold at all. */
  placement?: {
    label: string;
    isPlaced: (dayIdx: number, week: number) => boolean;
    onToggle: (dayIdx: number, week: number) => void;
  } | null;
}) {
  return useMemo(() => {
    const bundle = (
      name: string,
      label: string,
      run: () => void,
    ): GridGestureBundle => ({
      onLongPress: () => run(),
      accessibilityActions: [{ name, label }],
      onAccessibilityAction: (e) => {
        if (e.nativeEvent.actionName === name) run();
      },
    });

    const askRemoveWeek = async (week: number) => {
      if (!confirm || !onRemoveWeek) return;
      const ok = await confirm({
        title: t("composer.removeWeek", { defaultValue: "Remove week" }),
        body: t("composer.removeWeekConfirm", {
          defaultValue: "Confirm — drop this week and its sets",
        }),
        confirmLabel: t("composer.remove", { defaultValue: "Remove" }),
        cancelLabel: t("mobile.common.cancel", { defaultValue: "Cancel" }),
      });
      if (ok) onRemoveWeek(week);
    };
    const askRemoveDay = async (dayIdx: number) => {
      if (!confirm || !onRemoveDay) return;
      const ok = await confirm({
        title: t("composer.removeDay", {
          n: dayIdx + 1,
          defaultValue: "Remove day D{{n}}",
        }),
        body: t("composer.removeDayConfirm", {
          n: dayIdx + 1,
          defaultValue: "Confirm — remove day D{{n}}?",
        }),
        confirmLabel: t("composer.remove", { defaultValue: "Remove" }),
        cancelLabel: t("mobile.common.cancel", { defaultValue: "Cancel" }),
      });
      if (ok) onRemoveDay(dayIdx);
    };

    return {
      /** Spread onto the week head. Empty ⇒ that week cannot be removed. */
      weekRemoval: (week: number) =>
        canRemoveWeek
          ? bundle(
              "remove",
              t("composer.removeWeek", { defaultValue: "Remove week" }),
              () => void askRemoveWeek(week),
            )
          : {},
      /** Spread onto the pinned day button. Empty ⇒ that day is the last one. */
      dayRemoval: (dayIdx: number) =>
        canRemoveDay
          ? bundle(
              "remove",
              t("composer.removeDay", {
                n: dayIdx + 1,
                defaultValue: "Remove day D{{n}}",
              }),
              () => void askRemoveDay(dayIdx),
            )
          : {},
      /** Spread onto a cell. Empty ⇒ nothing is focused, so there is nothing to
       *  place — the cell keeps its tap (move the cursor) and gains no hold.
       *  ⛔ The action NAMES THE EXERCISE, because the visible cell is a
       *  coordinate: "D2 · W1" says where, never WHAT is being placed there.
       *  The dead PlacementMatrix said the name, and dropping it in PE-27 was a
       *  quiet a11y regression the ToggleRow only half repaid. */
      /** Spread onto the `W ＋` chip. Empty ⇒ no clone handler was wired. */
      weekClone: (): GridGestureBundle =>
        onCloneWeek
          ? bundle(
              "clone",
              t("composer.cloneWeekAction", {
                defaultValue: "Add a week repeating this one",
              }),
              onCloneWeek,
            )
          : {},
      cellPlacement: (dayIdx: number, week: number) =>
        placement
          ? bundle(
              "placement",
              placement.isPlaced(dayIdx, week)
                ? t("composer.placementRemoveHere", {
                    exercise: placement.label,
                    defaultValue: "Remove {{exercise}} from here",
                  })
                : t("composer.placementAddHere", {
                    exercise: placement.label,
                    defaultValue: "Place {{exercise}} here",
                  }),
              () => placement.onToggle(dayIdx, week),
            )
          : {},
    };
  }, [
    t,
    confirm,
    canRemoveWeek,
    canRemoveDay,
    onRemoveWeek,
    onRemoveDay,
    onCloneWeek,
    placement,
  ]);
}
